import React, { useState, useEffect } from 'react';
import { Megaphone, Plus, Trash2, X, CheckCircle, AlertCircle, Users } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import Modal from '../../components/Modal';
const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
    </div>
  );
}

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', batch_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchAll = async () => {
    try {
      const [annRes, batRes] = await Promise.all([
        axios.get(`${API}/api/admin/announcements`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/admin/batches`, { headers: getAuthHeaders() }),
      ]);
      setAnnouncements(annRes.data);
      setBatches(batRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      setToast({ message: 'Please fill in title and message.', type: 'error' }); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/announcements`,
        { title: form.title.trim(), message: form.message.trim(), batch_id: form.batch_id || null },
        { headers: getAuthHeaders() }
      );
      setToast({ message: 'Announcement posted!', type: 'success' });
      setForm({ title: '', message: '', batch_id: '' });
      setShowForm(false);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Something went wrong.', type: 'error' });
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/api/admin/announcements/${id}`, { headers: getAuthHeaders() });
      setToast({ message: 'Announcement deleted.', type: 'success' });
      fetchAll();
    } catch { setToast({ message: 'Could not delete. Try again.', type: 'error' }); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Broadcast</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Announcements</h1>
          <p className="text-[#57534E] text-sm font-body mt-1">Share updates, reminders, and motivation.</p>
        </div>
        <button data-testid="post-announcement-button" onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-5 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all">
          <Plus className="w-4 h-4" strokeWidth={2} /> Post
        </button>
      </div>

      {showForm && (
        <Modal
          title="New Announcement"
          onClose={() => setShowForm(false)}
          footer={
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm">Cancel</button>
              <button data-testid="submit-announcement" type="submit" form="announcement-form" disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Posting...' : 'Post Now'}
              </button>
            </div>
          }
        >
          <form id="announcement-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Title</label>
              <input data-testid="ann-title-input" type="text" placeholder="e.g. Class cancelled tomorrow"
                value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Message</label>
              <textarea data-testid="ann-message-input" rows={4} placeholder="Write your message here..."
                value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Audience</label>
              <select data-testid="ann-batch-select" value={form.batch_id}
                onChange={e => setForm(f => ({ ...f, batch_id: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body">
                <option value="">All Students</option>
                {batches.map(b => <option key={b.id} value={b.id}>{b.name} only</option>)}
              </select>
            </div>
          </form>
        </Modal>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : announcements.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
          <Megaphone className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-heading font-medium text-[#292524]">No announcements yet</p>
          <p className="text-[#78716C] text-sm font-body mt-1">Post your first update to your students!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(ann => (
            <div key={ann.id} data-testid={`announcement-card-${ann.id}`}
              className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50 hover:shadow-card-hover transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 bg-[#FFEDD5] rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                    <Megaphone className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-heading font-semibold text-[#292524] text-sm">{ann.title}</p>
                      {ann.batch_name ? (
                        <span className="text-xs bg-purple-100 text-purple-700 rounded-full px-2.5 py-0.5 font-body">{ann.batch_name}</span>
                      ) : (
                        <span className="text-xs bg-[#FFEDD5] text-[#D97706] rounded-full px-2.5 py-0.5 font-body flex items-center gap-1">
                          <Users className="w-3 h-3" strokeWidth={1.5} />All
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#57534E] font-body leading-relaxed">{ann.message}</p>
                    <p className="text-xs text-[#A8A29E] font-body mt-2">{timeAgo(ann.created_at)}</p>
                  </div>
                </div>
                <button data-testid={`delete-ann-${ann.id}`} onClick={() => handleDelete(ann.id)}
                  className="text-[#A8A29E] hover:text-red-500 transition-colors p-1 shrink-0">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
